import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/others/Badges";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@/components/ui/library/card";
import { FileText, Upload, Eye, Download, Loader2 } from "lucide-react";
import { useActiveDataMutation } from "@/features/leads/hook/useActiveDataMutation";
import { useRequirementsByLeadOrChild } from "@/features/stage/api/useRequirements";
import { useDocumentMutaion } from "@/hooks/api/useDocmentMutation";
import { refetchCurrentLead } from "@/features/leads/api/useCurrentLead";
import { useMemo } from "react";
import { getFileTypeColor } from "@/utils/convetors";

export default function RequiredDocumentsCard() {
  const { lead, documents, isChildActive, activeData } =
    useActiveDataMutation();
  const { uploadFile } = useDocumentMutaion();
  const { requirements } = useRequirementsByLeadOrChild(
    activeData?.id,
    isChildActive ? "child" : "lead"
  );

  const docRequirements = useMemo(() => {
    if (!requirements || typeof requirements !== "object") return [];
    const docReqs = (requirements as any)?.Documents;
    return Array.isArray(docReqs) ? docReqs : [];
  }, [requirements]);

  const getUploadedDoc = (referenceKey: string) =>
    documents?.find(
      (doc: any) => doc.name?.toLowerCase() === referenceKey.toLowerCase()
    );

  const handleFileUpload = (file: File, referenceKey: string) => {
    if (!lead) return;

    uploadFile.mutate(
      {
        file,
        attachedTo: lead.modelName,
        attachedScope: lead.id,
        category: lead.stage?.name || "General",
        name: referenceKey,
      },
      {
        onSuccess: () => refetchCurrentLead(),
      }
    );
  };

  if (docRequirements.length === 0) return null;

  return (
    <Card className="border-[#e6e8e7] rounded-[16px] gap-1 py-4 shadow-none">
      <CardHeader className="px-4 pb-0">
        <CardTitle className="text-[16px] font-medium text-black tracking-[-0.32px] leading-normal">
          Required Documents
        </CardTitle>
      </CardHeader>
      <CardContent className="px-4 pt-0">
        <div className="flex flex-col gap-3">
          {docRequirements.map((req: any) => {
            const uploadedDoc = getUploadedDoc(req.referenceKey);
            const isUploaded = !!uploadedDoc;
            const fileType =
              uploadedDoc?.type ||
              uploadedDoc?.fileName?.split(".").pop()?.toUpperCase() ||
              "";

            return (
              <div
                key={req.id}
                className="bg-[rgba(40,12,242,0.02)] border border-[rgba(40,12,242,0.06)] rounded-[12px] px-3 py-1.5 flex items-center gap-2"
              >
                <div className="relative size-7 shrink-0">
                  <FileText
                    className="size-7 text-[#cacfd8]"
                    strokeWidth={1.5}
                  />
                  <Badge
                    className={`absolute bottom-0 left-0 ${
                      isUploaded ? getFileTypeColor(fileType) : "bg-[#fb3748]"
                    } !rounded-[3px] !px-1.5 !py-0.5 !text-white !text-[8px] !font-medium uppercase !leading-tight tracking-tight !h-auto border-none`}
                  >
                    {isUploaded ? fileType : "--"}
                  </Badge>
                </div>

                <div className="flex-1 min-w-0">
                  <p className="text-[14px] font-medium text-[#051912] tracking-[-0.56px] leading-normal">
                    {req.referenceKey}
                  </p>
                </div>

                {isUploaded ? (
                  <>
                    <a
                      href={uploadedDoc.fileUrl}
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="size-9 border-none"
                        style={{
                          backgroundImage:
                            "linear-gradient(160.304deg, rgba(38, 115, 85, 0.01) 110.38%, rgba(38, 115, 85, 0.12) 110.92%)",
                        }}
                      >
                        <Eye
                          className="size-6 text-[var(--color-black-8)]"
                          strokeWidth={1.5}
                        />
                      </Button>
                    </a>
                    <a
                      href={uploadedDoc.fileUrl}
                      download
                      target="_blank"
                      rel="noreferrer"
                    >
                      <Button
                        variant="ghost"
                        size="icon-sm"
                        className="size-9 border-none"
                        style={{
                          backgroundImage:
                            "linear-gradient(160.304deg, rgba(38, 115, 85, 0.01) 110.38%, rgba(38, 115, 85, 0.12) 110.92%)",
                        }}
                      >
                        <Download
                          className="size-4 text-[var(--color-black-8)]"
                          strokeWidth={2}
                        />
                      </Button>
                    </a>
                  </>
                ) : (
                  <label htmlFor={`file-upload-req-${req.id}`}>
                    <Button
                      type="button"
                      variant="ghost"
                      className="h-9 px-4 gap-2 border-none pointer-events-none"
                      style={{
                        backgroundImage:
                          "linear-gradient(173.195deg, rgba(38, 115, 85, 0.01) 110.38%, rgba(38, 115, 85, 0.12) 110.92%)",
                      }}
                    >
                      {uploadFile.isPending ? (
                        <Loader2 className="size-4 animate-spin text-[#051912]" />
                      ) : (
                        <Upload
                          className="size-4 text-[#051912]"
                          strokeWidth={2}
                        />
                      )}
                      <span className="text-[14px] font-medium text-[#051912] tracking-[-0.56px]">
                        Upload
                      </span>
                    </Button>
                    <input
                      id={`file-upload-req-${req.id}`}
                      type="file"
                      className="hidden"
                      accept=".pdf,.jpg,.jpeg,.png"
                      onChange={(e) => {
                        const file = e.target.files?.[0];
                        if (file) {
                          handleFileUpload(file, req.referenceKey);
                        }
                        e.target.value = "";
                      }}
                      disabled={uploadFile.isPending}
                    />
                  </label>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}
